'use client';
import { IMaker } from '@/src/interfaces/generated/contentful';
import {
  Box,
  Heading,
  Stack,
  RichText,
  PageSection,
  PageSectionTitle,
  Link,
  Paragraph,
} from '@/src/packages/components';
import { ReactNode } from 'react';
import { Text } from '@/src/packages/components/ui/Text';
import Image from 'next/image';
import { Video } from '@/src/packages/components/ui/Video';
import { TemplateSidebar } from '@/src/packages/components/templates/TemplateSidebar';
import { ManufacturingLinks } from '@/src/packages/components/ui/Footer/Footer.config';
import { RichTextConfigArticle } from '@/src/packages/components/ui/RichText/RichText.config';
import { CertificationListItem } from '@/src/packages/components/pages/Certifications';
import { MakerCard } from '@/src/packages/components/pages/Makers';
import { PageSectionStack } from '@/src/packages/components/templates/PageSectionStack';
import { getRoute } from '@/src/packages/utils/routes';
import { paths } from '@/src/packages/routes';

const Attribute = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => {
  return (
    <Box className="flex-1 space-y-1">
      <Heading as="h3" className="leading-none" fontWeight="medium">
        {title}
      </Heading>

      <Text leading="none">{children}</Text>
    </Box>
  );
};

type IMakerProps = {
  maker: IMaker;
  related: IMaker[];
};

const MakerProfileAttributes = ({ maker }: { maker: IMaker }) => {
  const url = new URL(maker.fields.website);
  return (
    <Box className="space-y-4">
      <Stack className="space-y-4">
        <Attribute title="Country">
          {maker.fields.country.fields.name}
        </Attribute>
        <Attribute title="Founded">{maker.fields.founded}</Attribute>
        {maker.fields.employees && (
          <Attribute title="Employees">{maker.fields.employees}</Attribute>
        )}
        <Attribute title="Website">
          <Link variant="solid" asChild>
            <Box
              as="a"
              href={maker.fields.website}
              target="_blank"
              className="inline-block"
            >
              {url.hostname}
            </Box>
          </Link>
        </Attribute>
      </Stack>
      <Box className="text-text-secondary text-xs">
        The images, logos, videos, and other media displayed on this page are
        the property of their respective owners, and are used here with their
        explicit consent. Any unauthorized use or reproduction of these
        materials is strictly prohibited and may violate copyright laws.
      </Box>
    </Box>
  );
};

const MakerProfileLogo = ({ maker }: { maker: IMaker }) => {
  return (
    <Box className="relative aspect-square border">
      <Image
        src={maker.fields.logo.fields.file.url}
        className="object-cover"
        alt={`${maker.fields.name} logo`}
        fill
        priority
      />
    </Box>
  );
};

const MakerProfileSidebar = ({ maker }: { maker: IMaker }) => {
  return (
    <Box className="hidden w-[400px] 2xl:block">
      <Box className="top-gutter space-y-gutter sticky">
        <Box className="relative aspect-square border">
          <Image
            src={maker.fields.logo.fields.file.url}
            className="object-cover"
            alt={`${maker.fields.name} logo`}
            fill
            priority
            sizes="100vw"
          />
        </Box>

        <MakerProfileAttributes maker={maker} />
      </Box>
    </Box>
  );
};

const MakerMasthead = ({ maker }: { maker: IMaker }) => {
  return (
    <Box className="relative h-[400px]">
      <Image
        src={maker.fields.cover.fields.file.url}
        className="object-cover"
        alt={`${maker.fields.name} cover photo`}
        fill
        priority
        sizes="100vw"
      />
    </Box>
  );
};

const MakerCertifications = ({ maker }: { maker: IMaker }) => {
  if (!maker.fields.certifications || maker.fields.certifications.length === 0)
    return <></>;

  return (
    <PageSection>
      <PageSectionTitle>Certifications</PageSectionTitle>

      <Box>
        {maker.fields.certifications?.map((certification, i) => {
          return (
            <CertificationListItem key={i} certification={certification} />
          );
        })}
      </Box>
    </PageSection>
  );
};

const MakerRelated = ({ related }: { related: IMaker[] }) => {
  return (
    <PageSection>
      <PageSectionTitle>See more suppliers</PageSectionTitle>
      <Box className="gap-gutter grid grid-cols-12">
        {related.map((item, i) => {
          return (
            <Box className="col-span-6" key={i}>
              <MakerCard item={item} />
            </Box>
          );
        })}
      </Box>
    </PageSection>
  );
};

const MakerFactory = ({ maker }: { maker: IMaker }) => {
  return (
    <PageSection>
      <Box className="space-y-gutter">
        {maker.fields.videoUrl && (
          <Box className="aspect-video">
            <Video url={maker.fields.videoUrl} controls />
          </Box>
        )}

        {maker.fields.images &&
          maker.fields.images.map((image, i) => {
            return (
              <Box className="relative aspect-[4/3]" key={i}>
                <Image
                  src={image.fields.file.url}
                  className="object-cover"
                  alt=""
                  fill
                  priority
                  sizes="100vw"
                />
              </Box>
            );
          })}
      </Box>
    </PageSection>
  );
};

const MakerProfileIntro = ({ maker }: { maker: IMaker }) => {
  return (
    <Box className="space-y-gutter">
      <MakerMasthead maker={maker} />
      <Box className="space-y-4">
        <Heading as="h1" className="font-serif text-4xl leading-none">
          {maker.fields.name}
        </Heading>
        <RichText
          data={maker?.fields.content}
          options={RichTextConfigArticle}
        />
      </Box>
      <Box className="gap-gutter flex 2xl:hidden">
        <Box className="w-[140px]">
          <MakerProfileLogo maker={maker} />
        </Box>
        <Box className="flex-1">
          <MakerProfileAttributes maker={maker} />
        </Box>
      </Box>
    </Box>
  );
};

const MakerProfileMain = ({
  maker,
  related,
}: {
  maker: IMaker;
  related: IMaker[];
}) => {
  return (
    <Box className="space-y-gutter flex-1">
      <PageSectionStack>
        <MakerProfileIntro maker={maker} />
        <MakerFactory maker={maker} />
        {/* <MakerCertifications maker={maker} /> */}
        {/* <MakerRelated related={related} /> */}
      </PageSectionStack>
    </Box>
  );
};

export const MakerPage = (props: IMakerProps) => {
  const { maker, related } = props;

  return (
    <TemplateSidebar
      links={ManufacturingLinks}
      breadcrumb={[
        {
          label: 'Makers',
          path: getRoute({ route: paths.MAKERS_INDEX }),
        },
        {
          label: maker.fields.name,
        },
      ]}
    >
      <Box className="space-y-gutter">
        <Box className="gap-gutter flex flex-col 2xl:flex-row">
          <MakerProfileMain maker={maker} related={related} />
          <MakerProfileSidebar maker={maker} />
        </Box>
      </Box>
    </TemplateSidebar>
  );
};
